import { MODEL_VERSION, SCENE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { TaskParams } from '@/app/types';
import { isImageToVideoTaskType } from '@/app/utils';

interface LimitedTrialParams {
  modelVersion: string;
  isFree: boolean;
  settings: TaskParams['settings'];
  isProMode: boolean;
  taskType: TaskParams['type'];
}

/**
 * 判断是否需要消耗体验次数和消耗积分，仅支持普通任务，而不是模版任务
 * 返回值说明：
 * isInTrials: 是否需要消耗体验次数
 * needCredits: 是否需要消耗积分
 */
export const isLimitedTrial = ({
  modelVersion,
  isFree,
  settings,
  isProMode,
  taskType,
}: LimitedTrialParams): {
  isInTrials: boolean;
  // 有试用次数，不代表0积分
  needCredits?: boolean;
} => {
  const {
    remainingTrials,
    remainingTrialsQ1,
    trialsProMode,
    remainingTrialsQ2TransitionSpeed,
    remainingTrialsQ2TransitionPro,
    remainingTrialsQ2T2V,
    remainingTrialsQ2C2V,
    remainingTrialsQ2T2I,
    remainingTrialsQ2R2I,
    remainingTrialsText2Audio,
    remainingTrialsQ2Pro,
    remainingTrialsQ3T2VI2V,
    remainingTrialsQ3C2V,
    trialsQ3T2VI2VDurations,
    trialsQ3C2VDurations,
  } = useStore((state) => ({
    remainingTrials: state.trials720p?.remaining,
    remainingTrialsQ1: state.trialsQ1?.remaining,
    trialsProMode: state.trialsProMode,
    remainingTrialsQ2TransitionSpeed: state.trialsQ2TransitionSpeed?.remaining,
    remainingTrialsQ2TransitionPro: state.trialsQ2TransitionPro?.remaining,
    remainingTrialsQ2T2V: state.trialsQ2T2V?.remaining,
    remainingTrialsQ2C2V: state.trialsQ2C2V?.remaining,
    remainingTrialsQ2T2I: state.trialsQ2T2I?.remaining,
    remainingTrialsQ2R2I: state.trialsQ2R2I?.remaining,
    remainingTrialsText2Audio: state.trialsText2Audio?.remaining,
    remainingTrialsQ2Pro: state.trialsQ2Pro?.remaining,
    remainingTrialsQ3T2VI2V: state.trialsQ3T2VI2V?.remaining,
    remainingTrialsQ3C2V: state.trialsQ3C2V?.remaining,
    trialsQ3T2VI2VDurations: state.trialsQ3T2VI2V?.durations,
    trialsQ3C2VDurations: state.trialsQ3C2V?.durations,
  }));

  // 模版任务没有试用次数的概念
  if (taskType === SCENE) {
    return {
      isInTrials: false,
    };
  }
  if (taskType === 'text2audio') {
    return {
      isInTrials: remainingTrialsText2Audio > 0,
    };
  }

  // 专业模式下，消耗专业模式的次数（仅对免费用户生效）
  if (isFree && isProMode) {
    return {
      isInTrials: trialsProMode?.remaining > 0,
      // 专业模式需要积分
      needCredits: true,
    };
  }

  // 除了2.0模型外，其他模型只有生成单个样本时，才消耗次数
  if (modelVersion !== MODEL_VERSION['2.0'] && settings?.sample_count === 1) {
    if (settings?.schedule_mode === 'nopeak') {
      //错峰模式，不消耗次数
      return {
        isInTrials: false,
      };
    }

    if (modelVersion === MODEL_VERSION.Q2_PRO && taskType === 'character2video') {
      return {
        isInTrials: remainingTrialsQ2Pro > 0,
      };
    }

    if (modelVersion === MODEL_VERSION.Q3 && settings?.duration) {
      if (taskType === 'text2video' || taskType === 'img2video' || taskType === 'headtailimg2video') {
        const isInTrials =
          remainingTrialsQ3T2VI2V > 0 && (trialsQ3T2VI2VDurations?.includes(Number(settings.duration)) ?? false);
        return {
          isInTrials,
        };
      }

      if (taskType === 'character2video') {
        const isInTrials =
          remainingTrialsQ3C2V > 0 && (trialsQ3C2VDurations?.includes(Number(settings.duration)) ?? false);
        return {
          isInTrials,
        };
      }
    }

    if (modelVersion === MODEL_VERSION.Q2) {
      const isI2V = taskType === 'img2video' || taskType === 'headtailimg2video'; // 只有 img2video 和 headtailimg2video 可以试用，multi_frame 不能试用
      if (isI2V) {
        // 免费+付费用户均可0积分使用新功能
        if (settings?.transition === 'speed') {
          return {
            isInTrials: remainingTrialsQ2TransitionSpeed > 0,
          };
        }
        // 免费用户可0积分试用，付费用户无0积分体验次数
        if (settings?.transition === 'pro') {
          return {
            isInTrials: remainingTrialsQ2TransitionPro > 0,
          };
        }
      }

      if (taskType === 'text2video') {
        return {
          isInTrials: remainingTrialsQ2T2V > 0,
        };
      }

      if (taskType === 'character2video') {
        return {
          isInTrials: remainingTrialsQ2C2V > 0,
        };
      }

      if (taskType === 'text2image') {
        return {
          isInTrials: remainingTrialsQ2T2I > 0,
        };
      }

      if (taskType === 'reference2image') {
        return {
          isInTrials: remainingTrialsQ2R2I > 0,
        };
      }
    }

    if (modelVersion === MODEL_VERSION.Q1) {
      // Q1 免费试用，对付费 / 免费用户都生效
      return {
        isInTrials:
          (isImageToVideoTaskType(taskType) || taskType === 'text2video' || taskType === 'character2video') &&
          remainingTrialsQ1 > 0,
      };
    }

    return {
      isInTrials: false,
    };
  }

  // 720p 分辨率时，消耗 720p 的次数（仅对免费用户生效）
  const trials720p = isFree && settings.resolution === '720p' && remainingTrials > 0;
  return {
    isInTrials: trials720p,
    needCredits: true,
  };
};
